import Dexie from 'dexie';

import { db } from '../db.ts';
import type { OutboxItem } from '../db.ts';

/**
 * Shared internals for the entry repositories.
 *
 * The rule this file exists to enforce: **a record write and its outbox item
 * are one transaction.** A record that lands without an outbox item never
 * reaches the server, and the trainee has no way of knowing — the app shows the
 * entry, the sync indicator says all is well, and the entry exists on exactly
 * one device until that device is lost. That is hazard H2 in COMPLIANCE.md, and
 * it is the bug this phase exists to prevent.
 */

export type Entity = 'case' | 'procedure' | 'edt' | 'anaesthetic' | 'communication';

export function outboxItem(entity: Entity, entityId: string, userId: string): OutboxItem {
  return {
    op: 'upsert',
    entity,
    entityId,
    userId,
    status: 'pending',
    attempts: 0,
    createdAt: new Date(),
  };
}

/**
 * Runs a write and its outbox item atomically.
 *
 * If either fails, neither happens. Callers must do all their writing inside
 * `work` and must not swallow errors within it, or the guarantee is lost.
 */
/** The Dexie table each outbox entity kind refers to. Defined once. */
export function tableFor(entity: Entity) {
  if (entity === 'case') return db.cases;
  if (entity === 'procedure') return db.procedures;
  if (entity === 'edt') return db.edtEntries;
  if (entity === 'communication') return db.communications;
  return db.anaestheticCases;
}

export function writeWithOutbox<T>(
  entity: Entity,
  entityId: string,
  userId: string,
  work: () => Promise<T>,
): Promise<T> {
  const table = tableFor(entity);

  return db
    .transaction('rw', table, db.outbox, async () => {
      const result = await work();
      await db.outbox.add(outboxItem(entity, entityId, userId));
      return result;
    })
    .then((result) => {
      notifyLocalWrite();
      return result;
    });
}

/** A record belongs to this user and has not been deleted. */
export function isLive(
  record: { userId: string; deletedAt?: Date | undefined },
  userId: string,
): boolean {
  return record.userId === userId && record.deletedAt === undefined;
}

/**
 * The bounds that select every row for one user from a `[userId+occurredAt]`
 * compound index. `Dexie.minKey`/`maxKey` are the documented way to say "any
 * value" for the second component.
 */
export function userRange(userId: string): [[string, unknown], [string, unknown]] {
  return [
    [userId, Dexie.minKey],
    [userId, Dexie.maxKey],
  ];
}

/**
 * Newest first, which is the order every screen wants — the entry list, the
 * recently-used procedures in the picker, and the PDF appendix.
 */
export function byOccurredAtDescending(a: { occurredAt: Date }, b: { occurredAt: Date }): number {
  return b.occurredAt.getTime() - a.occurredAt.getTime();
}

/**
 * Fires after any local write, so the sync engine can drain the outbox
 * immediately rather than waiting for its five-minute tick.
 *
 * A callback list rather than an import of the engine: the repositories are the
 * bottom of the stack and must not depend on the thing above them. If nobody is
 * listening — in a test, or before sign-in — writes simply carry on.
 */
const writeListeners = new Set<() => void>();

export function onLocalWrite(listener: () => void): () => void {
  writeListeners.add(listener);
  return () => writeListeners.delete(listener);
}

export function notifyLocalWrite(): void {
  for (const listener of writeListeners) listener();
}
